import { useEffect } from "react";
import { Link } from "react-router";
import { styled } from "styled-components";

import { SectionTitleUnderlined } from "common/components";
import { breakpoints } from "common/styles";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { getUserId } from "domains/authentication/selectors";
import { getSiteLanguage } from "domains/language/selectors";
import { fetchEmergingThemes } from "domains/monitor/emergingThemes/actions";
import { EmergingThemeCarousel } from "domains/monitor/emergingThemes/components/EmergingThemeCarousel";
import { EmergingThemesList } from "domains/monitor/emergingThemes/components/EmergingThemesList";
import { getEmergingThemesIsPending, getEmergingThemesItems } from "domains/monitor/emergingThemes/selectors";
import { getTopicsFilterSelectedIds } from "domains/monitor/filter/selectors";
import { getFollowedTopicsIds } from "entities/topics/selectors";

const StyledContainer = styled.div`
  margin: 0;
  box-sizing: border-box;

  @media ${breakpoints.medium} {
    display: flex;
    flex-direction: column;
    flex: 5;
    margin-left: 2rem;
  }

  @media ${breakpoints.extraLarge} {
    margin-left: 3rem;
  }
`;

const StyledTitleContainer = styled.div`
  margin: 10px 1rem 0 1rem;

  @media ${breakpoints.medium} {
    margin: 0;
  }
`;

const StyledSeeAllButton = styled(Link)`
  display: block;
  color: ${({ theme }) => theme.colors.textFade};
  margin: 0;
  text-transform: uppercase;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colors.textBright};
  }

  @media ${breakpoints.medium} {
    display: none;
  }
`;

export const EmergingThemes = () => {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const selectedTopics = useSelector(getTopicsFilterSelectedIds);
  const isPending = useSelector(getEmergingThemesIsPending);
  const items = useSelector(getEmergingThemesItems);
  const language = useSelector(getSiteLanguage);
  const followingTopicsIds = useSelector(getFollowedTopicsIds);
  const userId = useSelector(getUserId);

  const followingTopicsDependency = followingTopicsIds.join();
  const selectedTopicsDependency = selectedTopics.join();

  useEffect(() => {
    dispatch(fetchEmergingThemes({ filters: { topic: selectedTopics }, offset: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicsDependency, language, userId, followingTopicsDependency]);

  const seeAllButton =
    items.length > 0 ? (
      <StyledSeeAllButton to="/monitor/emerging-themes">
        {translate("app.discover.topic.tabs.see_more")}
      </StyledSeeAllButton>
    ) : undefined;

  return (
    <StyledContainer>
      <StyledTitleContainer>
        <SectionTitleUnderlined isLoading={isPending} rightElement={seeAllButton}>
          {translate("app.monitor.emerging_themes")}
        </SectionTitleUnderlined>
      </StyledTitleContainer>
      <EmergingThemeCarousel />
      <EmergingThemesList />
    </StyledContainer>
  );
};
